import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'

interface ThemeBranding {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  logoUrl: string
  backgroundImage: string
  appName: string
  borderRadius: 'rounded' | 'square'
  shadowIntensity: 'strong' | 'subtle'
  animationsEnabled: boolean
  gradients: {
    blue: string
    orange: string
    green: string
  }
}

interface StatusColors {
  approved: string
  denied: string
  pending: string
  conditional: string
  green: string
  yellow: string
  red: string
}

type ThemeMode = 'light' | 'dark' | 'system'

const defaultTheme: ThemeBranding = {
  primaryColor: 'blue',
  secondaryColor: 'gray',
  accentColor: 'green',
  logoUrl: '/logo-primary.svg',
  backgroundImage: '',
  appName: 'Risk & Complexity Assessment',
  borderRadius: 'rounded',
  shadowIntensity: 'strong',
  animationsEnabled: true,
  gradients: {
    blue: 'bg-gradient-to-br from-blue-700 to-blue-800',
    orange: 'bg-gradient-to-br from-orange-600 to-orange-700',
    green: 'bg-gradient-to-br from-emerald-600 to-emerald-700',
  },
}

const defaultStatusColors: StatusColors = {
  approved: 'emerald',
  denied: 'red',
  pending: 'amber',
  conditional: 'blue',
  green: 'emerald',
  yellow: 'amber',
  red: 'red',
}

interface ThemeContextType {
  theme: ThemeBranding
  themeMode: ThemeMode
  updateTheme: (updates: Partial<ThemeBranding>) => void
  resetTheme: () => void
  setThemeMode: (mode: ThemeMode) => void
  isDarkMode: boolean
  statusColors: StatusColors
  getStatusColorClass: (status: string, type: 'bg' | 'text' | 'border') => string
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeBranding>(() => {
    const savedTheme = localStorage.getItem('appTheme')
    return savedTheme ? JSON.parse(savedTheme) : defaultTheme
  })

  const [statusColors] = useState<StatusColors>(defaultStatusColors)

  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode')
    return (savedMode as ThemeMode) || 'system'
  })

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (themeMode === 'dark') return true
    if (themeMode === 'light') return false
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  const getStatusColorClass = (status: string, type: 'bg' | 'text' | 'border') => {
    const statusLower = status.toLowerCase()
    let colorKey: keyof StatusColors = 'green'

    if (statusLower.includes('approved')) colorKey = 'approved'
    else if (statusLower.includes('denied')) colorKey = 'denied'
    else if (statusLower.includes('pending') || statusLower.includes('review')) colorKey = 'pending'
    else if (statusLower.includes('conditional')) colorKey = 'conditional'
    else if (statusLower.includes('green')) colorKey = 'green'
    else if (statusLower.includes('yellow')) colorKey = 'yellow'
    else if (statusLower.includes('red')) colorKey = 'red'

    const colorName = statusColors[colorKey]

    if (type === 'bg') {
      return `bg-${colorName}-600 hover:bg-${colorName}-700 text-white`
    } else if (type === 'text') {
      return isDarkMode ? `text-${colorName}-400` : `text-${colorName}-700`
    } else if (type === 'border') {
      return isDarkMode ? `border-${colorName}-500` : `border-${colorName}-600`
    }

    return ''
  }

  const updateTheme = (updates: Partial<ThemeBranding>) => {
    const newTheme = { ...theme, ...updates }
    setTheme(newTheme)
    localStorage.setItem('appTheme', JSON.stringify(newTheme))
  }

  const resetTheme = () => {
    setTheme(defaultTheme)
    localStorage.removeItem('appTheme')
  }

  const applyThemeMode = (mode: ThemeMode) => {
    if (typeof document === 'undefined' || !document.documentElement) return

    if (mode === 'dark') {
      document.documentElement.classList.add('dark')
      setIsDarkMode(true)
    } else if (mode === 'light') {
      document.documentElement.classList.remove('dark')
      setIsDarkMode(false)
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      if (prefersDark) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
      setIsDarkMode(prefersDark)
    }
  }

  const handleSetThemeMode = (mode: ThemeMode) => {
    setThemeMode(mode)
    localStorage.setItem('themeMode', mode)
    applyThemeMode(mode)
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      if (themeMode === 'system') {
        applyThemeMode('system')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    applyThemeMode(themeMode)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themeMode])

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeMode,
        updateTheme,
        resetTheme,
        setThemeMode: handleSetThemeMode,
        isDarkMode,
        statusColors,
        getStatusColorClass,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
